import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

const getToken = () => {
  return localStorage.getItem('token');
};

const clientAdminApiClient = axios.create({
  baseURL: config.api.clientServiceUrl,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 30000 // 30 seconds
});

clientAdminApiClient.interceptors.request.use(
  (config) => {
    const token = getToken();
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(clientAdminApiClient);

// Proactively refresh token before any request if within 30-minute expiry window
applyTokenRefreshInterceptor(clientAdminApiClient);

const clientAdminService = {
  /**
   * Fetch all clients with admin details
   * @returns {Promise<Array>} Array of client admin info
   */
  fetchAllClients: async () => {
    try {
      const response = await clientAdminApiClient.get('client-helper/admin/clients');
      return response.data;
    } catch (error) {
      console.error('Error fetching clients for admin:', error);
      throw error;
    }
  },

  /**
   * Fetch synced client-directory households for the create-client picker.
   * Shares the admin clients route (API Gateway is at its route quota).
   * @returns {Promise<Array<{account_id: ?string, household_name: string,
   *   contact_names: Array<string>, linked_client_id: ?string}>>}
   */
  fetchDirectoryHouseholds: async () => {
    try {
      const response = await clientAdminApiClient.get('client-helper/admin/clients', {
        params: { source: 'directory' }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching directory households:', error);
      throw error;
    }
  },

  /**
   * Create a new client
   * @param {Object} clientData - Client creation data
   * @param {string} clientData.client_name - Name of the client
   * @param {string} [clientData.description] - Optional description
   * @param {Array<string>} [clientData.nicknames] - Optional list of client nicknames
   * @param {string} [clientData.phone_number] - Optional E.164 phone number (+1XXXXXXXXXX)
   * @param {string} [clientData.tamarac_account_id] - Optional directory household to link
   * @returns {Promise<Object>} Created client info
   */
  createClient: async (clientData) => {
    try {
      const response = await clientAdminApiClient.post('client-helper/admin/clients', clientData);
      return response.data;
    } catch (error) {
      console.error('Error creating client:', error);
      throw error;
    }
  },

  /**
   * Update an existing client
   * @param {string} clientId - ID of the client to update
   * @param {Object} updateData - Client update data
   * @param {string} updateData.client_name - Updated name of the client
   * @param {string} [updateData.description] - Updated description
   * @param {Array<string>} [updateData.nicknames] - Updated list of client nicknames
   * @returns {Promise<Object>} Updated client info
   */
  updateClient: async (clientId, updateData) => {
    try {
      const response = await clientAdminApiClient.put(`client-helper/admin/clients/${clientId}`, updateData);
      return response.data;
    } catch (error) {
      console.error('Error updating client:', error);
      throw error;
    }
  },

  /**
   * Delete a client
   * @param {string} clientId - ID of the client to delete
   * @returns {Promise<Object>} Deletion confirmation
   */
  deleteClient: async (clientId) => {
    try {
      const response = await clientAdminApiClient.delete(`client-helper/admin/clients/${clientId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting client:', error);
      throw error;
    }
  }
};

export default clientAdminService;
